import React from 'react';
import Popup from '@/shared/components/layers/Popup/Popup';
import { useCompanyPlanTrend, TrendType } from './useCompanyPlanTrend';
import css from './CompanyPlanTrendPopup.module.css';
import LoadingOverlay from '../_shared/LoadingOverlay/LoadingOverlay';
import ErrorOverlay from '../_shared/ErrorOerlay/ErrorOerlay';
import { CompanyResult } from '../../../model/dashboard.types';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

function formatPeriodLabel(dateStart: string, dateEnd: string): string {
  const fmt = (s: string) => {
    const d = new Date(s + 'T12:00:00');
    return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short', year: 'numeric' });
  };
  return `${fmt(dateStart)} – ${fmt(dateEnd)}`;
}

function formatRuble(value: number): string {
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)} млн ₽`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(0)} тыс ₽`;
  return `${value.toLocaleString()} ₽`;
}

export type CompanyPlanTrendPopupProps = {
  open: boolean;
  onClose: () => void;
  dateStart: string | null;
  dateEnd: string | null;
  currentOpportunity: CompanyResult | null;
  currentProfit: CompanyResult | null;
};

const TREND_OPTIONS: { value: TrendType; label: string }[] = [
  { value: 'previous', label: 'Прошлый диапазон' },
  { value: 'lastYear', label: 'Аналогичный период (прошлый год)' },
];

const COLOR_CURRENT = 'var(--primary-color)';
const COLOR_TREND = 'var(--text-secondary-color)';

const CompanyPlanTrendPopup: React.FC<CompanyPlanTrendPopupProps> = ({
  open,
  onClose,
  dateStart,
  dateEnd,
  currentOpportunity,
  currentProfit,
}) => {
  const [trendType, setTrendType] = React.useState<TrendType>('previous');
  const { trendResult, trendLoading, trendError, trendDates } = useCompanyPlanTrend(
    open,
    dateStart,
    dateEnd,
    trendType
  );

  const chartData = React.useMemo(() => {
    const oppCur = currentOpportunity?.value ?? 0;
    const oppTr = trendResult.opportunity?.value ?? 0;
    const profCur = currentProfit?.value ?? 0;
    const profTr = trendResult.profit?.value ?? 0;
    return [
      { name: 'Оборот (факт)', current: oppCur, trend: oppTr, currentPct: currentOpportunity?.precent ?? 0, trendPct: trendResult.opportunity?.precent ?? 0 },
      { name: 'Прибыль (факт)', current: profCur, trend: profTr, currentPct: currentProfit?.precent ?? 0, trendPct: trendResult.profit?.precent ?? 0 },
    ];
  }, [currentOpportunity, currentProfit, trendResult]);

  const hasChartData = chartData.some((d) => d.current > 0 || d.trend > 0);

  return (
    <Popup open={open} onClose={onClose} title="Прошлый период плана компании" size="l" fullHeight={false}>
      <div className={css.wrap}>
        <div className={css.toggleWrap}>
          <span className={css.toggleLabel}>Сравнить с:</span>
          <div className={css.toggleGroup}>
            {TREND_OPTIONS.map((opt) => (
              <label key={opt.value} className={css.toggleOption}>
                <input
                  type="radio"
                  name="trendTypeCompany"
                  value={opt.value}
                  checked={trendType === opt.value}
                  onChange={() => setTrendType(opt.value)}
                />
                <span>{opt.label}</span>
              </label>
            ))}
          </div>
        </div>

        {trendLoading && <LoadingOverlay loading />}
        {trendError && <ErrorOverlay error={trendError} />}

        {!trendLoading && !trendError && trendDates && (
          <div className={css.periodLabel}>
            Прошлый период: {formatPeriodLabel(trendDates.dateStart, trendDates.dateEnd)}
          </div>
        )}

        {!trendLoading && !trendError && trendDates && hasChartData && (
          <div className={css.chartWrap}>
            <div className={css.chartTitle}>Факт выполнения, ₽</div>
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 12, right: 12, left: 12, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #eee)" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={(v) => formatRuble(v)} tick={{ fontSize: 11 }} />
                <Tooltip
                  formatter={(value: number | undefined) => (value != null ? formatRuble(value) : '')}
                  contentStyle={{ borderRadius: 8 }}
                />
                <Legend formatter={(value) => (value === 'current' ? 'Текущий период' : 'Прошлый период')} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="current" name="current" fill={COLOR_CURRENT} radius={[4, 4, 0, 0]} />
                <Bar dataKey="trend" name="trend" fill={COLOR_TREND} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className={css.chartTitle}>Выполнение, %</div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 12, right: 12, left: 12, bottom: 8 }} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color, #eee)" />
                <XAxis type="number" domain={[0, 100]} unit="%" tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} width={100} />
                <Tooltip
                  formatter={(value: number | undefined) => (value != null ? `${Number(value).toFixed(1)}%` : '')}
                  contentStyle={{ borderRadius: 8 }}
                />
                <Legend formatter={(value) => (value === 'currentPct' ? 'Текущий период' : 'Прошлый период')} wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="currentPct" name="currentPct" fill={COLOR_CURRENT} radius={[0, 4, 4, 0]} />
                <Bar dataKey="trendPct" name="trendPct" fill={COLOR_TREND} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}

        {!trendLoading && !trendError && trendDates && !hasChartData && (
          <div className={css.noData}>Нет данных для сравнения за выбранный период</div>
        )}
      </div>
    </Popup>
  );
};

export default CompanyPlanTrendPopup;
